// Runtime tenant resolution (Tenant Zero B7). The tenant is not baked into the build: it is the tenant of the
// signed-in session. Login returns member.tenant_slug/tenant_id; GET /me (the principal of the bearer) is the
// check at startup that the stored slug and the token still agree. VITE_NOMOS_TENANT is only a default.
import { AxiosError } from 'axios';
import { defaultTenantSlug, getMe, nomosFetch, resetTenantCaches, type NomosMe } from './client';
import {
  activate, activeSession, deactivate, dropActiveSession, lastTenantSlug, listSessions, saveSession, type NomosSession,
} from './sessions';

export interface LoginMember { member_id: string; tenant_id: string; tenant_slug?: string | null; tenant_name?: string | null; email: string }
export interface LoginResponse { token: string; expires_at: string | null; member?: LoginMember }

/** The organisation used when none is chosen (older API paths): last used here, else the build default, else ''. */
export function suggestedTenant(): string {
  return lastTenantSlug() ?? defaultTenantSlug();
}

/** An organisation offered after the password matched (display name; the slug is only sent back to NomOS). */
export interface TenantOption { slug: string; name: string }

/** POST /auth/login answered "choose a tenant": the password matched in several organisations. */
export class TenantChoiceRequired extends Error {
  constructor(readonly token: string, readonly tenants: TenantOption[], readonly expiresAt: string | null) {
    super('choose an organisation');
    this.name = 'TenantChoiceRequired';
  }
}

interface ChoiceResponse {
  choose_tenant: true;
  choice_token: string;
  expires_at: string | null;
  tenants: Array<{ tenant_slug: string; tenant_name?: string | null }>;
}

function hold(res: LoginResponse, fallbackSlug: string, email: string): void {
  saveSession({
    slug: res.member?.tenant_slug ?? fallbackSlug,
    token: res.token,
    tenant_id: res.member?.tenant_id ?? '',
    tenant_name: res.member?.tenant_name ?? null,
    email: res.member?.email ?? email,
    expires_at: res.expires_at,
  });
  resetTenantCaches();
}

/**
 * Email + password login (TZ-2: no organisation on the form). NomOS checks the password against every
 * membership of the email: one match → the session is held and returned; several → TenantChoiceRequired
 * (the organisations where the password matched); finish with chooseTenant().
 */
export async function signIn(email: string, password: string): Promise<LoginResponse> {
  const res = await nomosFetch<LoginResponse | ChoiceResponse>('POST', '/auth/login', { email, password }, { tenantScoped: false });
  if ('choose_tenant' in res && res.choose_tenant) {
    const tenants = res.tenants.map((t) => ({ slug: t.tenant_slug, name: t.tenant_name || t.tenant_slug }));
    rememberKnownTenants(tenants);
    throw new TenantChoiceRequired(res.choice_token, tenants, res.expires_at);
  }
  const ok = res as LoginResponse;
  hold(ok, ok.member?.tenant_slug ?? '', email);
  const slug = ok.member?.tenant_slug;
  if (slug) rememberKnownTenants([{ slug, name: ok.member?.tenant_name || slug }]);
  return ok;
}

/** Second step of signIn(): the single-use choice token + one offered organisation → its session. */
export async function chooseTenant(token: string, slug: string): Promise<LoginResponse> {
  const res = await nomosFetch<LoginResponse>('POST', '/auth/login/choose', { token, tenant_slug: slug }, { tenantScoped: false });
  hold(res, slug, res.member?.email ?? '');
  return res;
}

const KNOWN = 'nomos_known_tenants';

/** The organisations last offered to this browser's person at sign-in (for the switcher). */
export function knownTenants(): TenantOption[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KNOWN) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((t): t is TenantOption => !!t && typeof t.slug === 'string' && typeof t.name === 'string') : [];
  } catch { return []; }
}

function rememberKnownTenants(tenants: TenantOption[]): void {
  try { localStorage.setItem(KNOWN, JSON.stringify(tenants)); } catch { /* storage blocked */ }
}

/** POST /auth/login for `tenantSlug` (older body, for scripts/tests), then hold the session for that tenant. */
export async function loginToTenant(tenantSlug: string, email: string, password: string): Promise<LoginResponse> {
  const slug = tenantSlug.trim().toLowerCase();
  if (!slug) throw new AxiosError('Organisation is required', 'ERR_BAD_REQUEST');
  const res = await nomosFetch<LoginResponse>('POST', '/auth/login', { tenant_slug: slug, email, password }, { tenantScoped: false });
  hold(res, slug, email);
  return res;
}

/**
 * Resolve the active tenant from GET /me. The bearer decides the tenant: a stored active slug whose tenant_id
 * differs from the principal's is rejected (401), never guessed. A bearer with
 * no stored session (signed in before B7) is adopted only when the build default tenant's id matches /me.
 * Returns the session, or throws (the caller treats it like a 401).
 */
export async function resolveTenantFromMe(): Promise<{ me: NomosMe; session: NomosSession }> {
  const me = await getMe();
  const token = bearer();
  const current = activeSession();
  if (current) {
    if (current.tenant_id === me.tenant_id) return { me, session: current };
    if (!current.tenant_id) { const session = { ...current, tenant_id: me.tenant_id }; saveSession(session); return { me, session }; }
    throw unknownTenant(); // the stored slug names another tenant than the bearer's: never guess
  }
  const held = listSessions().find((s) => s.token === token && s.tenant_id === me.tenant_id);
  if (held) { activate(held.slug); resetTenantCaches(); return { me, session: held }; }
  const fallback = defaultTenantSlug();
  if (fallback) {
    const t = await nomosFetch<{ tenant_id: string; slug: string; name?: string }>(
      'GET', `/tenants/${encodeURIComponent(fallback)}`, undefined, { tenantScoped: false });
    if (t.tenant_id === me.tenant_id) {
      const session: NomosSession = { slug: t.slug, token, tenant_id: t.tenant_id, tenant_name: t.name ?? null, email: me.email };
      saveSession(session);
      resetTenantCaches();
      return { me, session };
    }
  }
  throw unknownTenant();
}

function bearer(): string {
  try { return localStorage.getItem('nomos_token') ?? ''; } catch { return ''; }
}

/** Shaped as a 401 so the seam's unauthorized handling signs the browser out of this session. */
function unknownTenant(): AxiosError {
  return new AxiosError('NomOS: the session does not belong to a known tenant', 'ERR_BAD_REQUEST', undefined, undefined,
    { status: 401, statusText: '', data: { detail: 'unknown tenant' }, headers: {}, config: {} as never } as never);
}

/** Tenants this person is signed in to in this browser. */
export function heldTenants(): Array<{ slug: string; name: string; active: boolean }> {
  const active = activeSession()?.slug;
  return listSessions().map((s) => ({ slug: s.slug, name: s.tenant_name || s.slug, active: s.slug === active }));
}

/**
 * The switcher's list: the organisations held here plus those last offered at sign-in (`held: false`
 * needs a password again). Empty for a person of one organisation — no switcher is shown then.
 */
export function switchableTenants(): Array<{ slug: string; name: string; active: boolean; held: boolean }> {
  const held = heldTenants().map((t) => ({ ...t, held: true }));
  const more = knownTenants().filter((k) => !held.some((h) => h.slug === k.slug)).map((k) => ({ ...k, active: false, held: false }));
  const all = [...held, ...more].sort((a, b) => a.name.localeCompare(b.name));
  return all.length > 1 ? all : [];
}

/** The email of the active session (pre-fills the login form when switching organisation). */
export function activeEmail(): string {
  return activeSession()?.email ?? '';
}

/** Switch to another held tenant. Caches are dropped; the caller reloads so React state starts clean. */
export function switchTenant(slug: string): boolean {
  if (!activate(slug)) return false;
  resetTenantCaches();
  return true;
}

/** Leave the active tenant to sign in to another one; the current session stays held and can be resumed. */
export function beginSignInToAnotherTenant(): void {
  deactivate();
  resetTenantCaches();
}

/** Revoke the active session on the server (best effort) and forget it; other tenants' sessions stay. */
export async function signOutActiveTenant(): Promise<void> {
  if (activeSession()) {
    try { await nomosFetch('POST', '/auth/logout', undefined, { tenantScoped: false }); } catch { /* already invalid */ }
  }
  dropActiveSession();
  resetTenantCaches();
}
